import { useTranslations } from '@aikoa/i18n/react'
import { Image } from 'expo-image'
import { View } from 'react-native'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Skeleton } from '~/components/ui/skeleton.tsx'
import { Text } from '~/components/ui/text'
import { useUserProfileById } from '~/data/user/userProfile'
import { cn } from '~/lib/utils'
import Icon, { type IconName } from '../Icon'

const aikoaAvatar = require('~/../assets/aikoa-realtor.png')

export default function RealtorCard({
  userId,
  size = 'default',
  className,
  showContactInfo = size === 'small' ? false : true,
}: {
  userId: string
  size?: 'small' | 'default'
  className?: string
  showContactInfo?: boolean
}) {
  const { t } = useTranslations('user')
  const { data: userProfile, isLoading, isError } = useUserProfileById(userId)

  const orgName = userProfile?.organisation?.name

  if (isLoading) {
    return (
      <View
        className={cn(
          'flex-row items-center gap-4 rounded-lg border border-border bg-card p-4',
          className
        )}>
        <Skeleton className="size-16 rounded-full" />
        <View className="flex-1 gap-2">
          <Skeleton className="h-6 w-32 rounded" />
          <Skeleton className="h-5 w-24 rounded" />
        </View>
      </View>
    )
  }

  if (isError || !userProfile) {
    return (
      <View
        className={cn(
          'h-28 flex-row items-center justify-center rounded-lg border border-border bg-card p-4',
          className
        )}>
        <Text>{t(($) => $.profile.realtor.notFound)}</Text>
      </View>
    )
  }

  const email = userProfile.email
  const phone = userProfile.phone

  const contactDetails = [
    email && {
      icon: 'mail-outline',
      value: email,
    },
    phone && {
      icon: 'phone-incoming-outline' satisfies IconName,
      value: phone,
    },
  ].filter(Boolean) as { icon: IconName; value: string }[]

  const hasContactInfo = showContactInfo && contactDetails.length > 0
  const shouldShowOrganisationName = size !== 'small' && !!orgName

  return (
    <View
      className={cn(
        'relative w-full overflow-hidden rounded-xl bg-card shadow-md',
        size === 'small' && 'items-start rounded-lg shadow-sm',
        className
      )}>
      <View
        className={cn('flex-row border-b border-tertiary/40', size === 'small' && 'border-b-0')}>
        <View className={cn('shrink-0 items-center justify-center gap-3 p-3')}>
          <Avatar
            className={cn('size-16', size === 'small' && 'size-8')}
            alt={t(($) => $.profile.realtor.avatar.alt, {
              name: userProfile.name,
            })}>
            <AvatarImage source={{ uri: userProfile.data.avatar_url }} />
            <AvatarFallback>
              <Text
                className={cn('font-semibold text-lg uppercase', size === 'small' && 'text-sm')}>
                {userProfile.name?.charAt(0) || 'V'}
              </Text>
            </AvatarFallback>
          </Avatar>
        </View>
        <View className={cn('flex-1 items-center justify-center', size !== 'small' && 'px-2 py-3')}>
          <View className={cn('flex-row items-center')}>
            <Text
              className={cn('font-medium text-lg', size === 'small' && 'font-medium text-base')}>
              {userProfile.name}
            </Text>
          </View>
          {userProfile.data.title && size !== 'small' && (
            <Text className="text-sm/normal text-secondary">{userProfile.data.title}</Text>
          )}
        </View>
      </View>
      {(shouldShowOrganisationName || hasContactInfo) && (
        <View
          className={cn(
            'items-center gap-2 p-2 pr-3',
            size !== 'small' && 'w-full flex-1 flex-row'
          )}>
          {shouldShowOrganisationName && (
            <View className={cn('shrink-0 flex-row items-center gap-2')}>
              <Image source={aikoaAvatar} className="size-12" />
              <Text className="whitespace-nowrap text-nowrap font-semibold text-sm/none">
                {orgName}
              </Text>
            </View>
          )}
          {hasContactInfo && (
            <View className={cn('flex-col gap-2', size !== 'small' && 'flex-1 justify-center')}>
              {contactDetails.map(({ icon, value }) => (
                <View
                  key={value}
                  className={cn(
                    'flex-row items-center gap-3',
                    size !== 'small' && 'w-full items-center justify-end'
                  )}>
                  <Icon name={icon as IconName} size={16} className="text-secondary" />
                  <Text className="text-sm/normal text-secondary">{value}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  )
}
